import { useCallback, useMemo, useState } from 'react';
import type { DateRangeShortcut } from '@/components/date/DateRange';
import type { Dayjs } from '@/config/dayjs';
import { useLocalization } from '@/hooks/useLocalization';
import { useToday } from '@/hooks/useToday';
import { parseAppDate } from '@/utils/date';
import {
  type NetWorthChangeRange,
  availableNetWorthChangeRanges,
  clampNetWorthChangePeriod,
  defaultNetWorthChangeRange,
  latestComparableDate,
  resolveNetWorthChangeRange,
} from './netWorthChangeRanges';
import type { NetWorthChangeSelection } from './netWorthChangeSelection';

/** Dates on the picker that are not yet a selection: half-picked or cleared. */
interface NetWorthChangeRangeDraft {
  startDate: string;
  endDate: string;
}

/** What the picker shows after Clear: both boxes empty. */
const CLEARED_DRAFT: NetWorthChangeRangeDraft = { startDate: '', endDate: '' };

interface NetWorthChangeRangePicker {
  /** Legacy's presets, in place of the shared picker's app-wide ones. */
  shortcutItems: DateRangeShortcut[];
  /** The saved preset's label, or empty for a custom range or a draft. */
  selectedShortcutLabel: string;
  /**
   * What the picker shows in place of the selection while a range is being
   * picked or after Clear, or null when it should show the selection.
   */
  draft: NetWorthChangeRangeDraft | null;
  /** Drops the draft, so the picker shows the selection again once closed. */
  onClose: () => void;
  /** Yesterday: nothing later has balances. */
  maxDate: Dayjs;
  /** The day the presets and limits count from; it moves on at midnight. */
  today: Dayjs;
  onChange: (
    startDate: string,
    endDate: string,
    shortcutLabel?: string,
  ) => void;
}

/**
 * Wires the shared `DateRange` picker to the widget's selection: which
 * presets it offers, which one it shows as chosen, and how a pick becomes a
 * selection.
 *
 * Presets resolve when the list is built, so the dates a shortcut fills in
 * are the ones the widget will ask for. A shortcut is stored as its preset,
 * so it re-resolves next session; any other pick is a custom range. A
 * half-picked range -- the start chosen, the end not yet -- is held as the
 * picker's draft until it is complete, so the picker shows what was picked
 * and the next click picks the end; the selection does not change, and the
 * draft is dropped when the picker closes. Clear empties both boxes, and they
 * stay empty once the picker closes; the widget still needs a range to load,
 * so it loads the default preset until another range is picked. So is a range that can never be
 * asked for: a typed date that does not parse, or an end before its start.
 * The previous selection stays, rather than an unusable one being saved.
 *
 * Everything counts from `useToday`, so a dashboard left open overnight moves
 * its presets and its latest pickable day on at midnight.
 */
export function useNetWorthChangeRangePicker(
  selection: NetWorthChangeSelection,
  setSelection: (selection: NetWorthChangeSelection) => void,
): NetWorthChangeRangePicker {
  const { t } = useLocalization();

  const rangeLabel = useCallback(
    (range: NetWorthChangeRange) => t(`netWorthChanges.range.${range}`, range),
    [t],
  );
  const today = useToday();
  const maxDate = useMemo(() => latestComparableDate(today), [today]);
  const presets = useMemo(() => availableNetWorthChangeRanges(today), [today]);

  const shortcutItems: DateRangeShortcut[] = useMemo(
    () =>
      presets.flatMap((range) => {
        const resolved = resolveNetWorthChangeRange(range, today);
        const start = parseAppDate(resolved?.startDate);
        const end = parseAppDate(resolved?.endDate);
        if (!start || !end) return [];
        return [{ label: rangeLabel(range), getValue: () => [start, end] }];
      }),
    [presets, rangeLabel, today],
  );

  const [draft, setDraft] = useState<NetWorthChangeRangeDraft | null>(null);
  const [isCleared, setIsCleared] = useState(false);

  const shownDraft = draft ?? (isCleared ? CLEARED_DRAFT : null);
  const savedShortcutLabel =
    selection.kind === 'preset' ? rangeLabel(selection.range) : '';
  const selectedShortcutLabel = shownDraft ? '' : savedShortcutLabel;

  const onChange = (
    startDate: string,
    endDate: string,
    shortcutLabel?: string,
  ) => {
    const preset = presets.find((range) => rangeLabel(range) === shortcutLabel);
    if (preset) {
      setDraft(null);
      setIsCleared(false);
      setSelection({ kind: 'preset', range: preset });
      return;
    }
    if (!startDate && !endDate) {
      setDraft(null);
      setIsCleared(true);
      setSelection({
        kind: 'preset',
        range: defaultNetWorthChangeRange(today),
      });
      return;
    }
    const period = { startDate, endDate };
    if (!startDate || !endDate || !clampNetWorthChangePeriod(period, today)) {
      setDraft(period);
      return;
    }
    setDraft(null);
    setIsCleared(false);
    setSelection({ kind: 'custom', period });
  };

  const onClose = useCallback(() => {
    setDraft(null);
  }, []);

  return {
    shortcutItems,
    selectedShortcutLabel,
    draft: shownDraft,
    onClose,
    maxDate,
    today,
    onChange,
  };
}
